// One conversation in the agent pane, started from a next action (Draft email, Draft
// reminder, Open email, Discuss). It is an ordinary project conversation: listed with the
// project's others and opened in full from here. A draft request starts as it opens;
// Discuss waits for the operator's first message.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import type { ActionDraft } from "../api/hooks/useProjectAgentStatus";
import { useAttachments } from "../api/hooks/useAttachments";
import {
  type RunEmail,
  useStatefulRun,
  useStatefulRunTurn,
} from "../api/hooks/useStatefulAgents";
import {
  projectConversationPath,
  projectSpecialistPath,
} from "../routes/learningAgentPaths";
import {
  type PaneConversation,
  paneActivity,
  useAgentPane,
  usePaneProjectName,
} from "../state/useAgentPane";
import { leftAfterSend } from "../utils/leftAfterSend";
import { agentDisplayLabel } from "../utils/runParticipants";
import { EmailCard } from "./RunItemCards";
import { LiveTurnPanel } from "./RunLivePanels";
import { RunComposer } from "./RunComposer";
import { useComposerQuote } from "./useComposerQuote";
import { RunTranscript } from "./RunTranscript";
import styles from "./AgentPaneConversation.module.css";

// A scheduled pass's draft, as the card a conversation's draft shows.
const actionEmail = (draft: ActionDraft): RunEmail => ({
  draft_id: draft.draft_id,
  author: draft.author,
  author_label: draft.author ? agentDisplayLabel(draft.author) : "",
  to: draft.to,
  subject: draft.subject,
  body: draft.body,
  trigger: "",
  why: draft.why,
  depends_on: [],
  merges: [],
  thread: draft.thread,
  discharges: [],
  previous_draft_id: null,
  first_drafted_at: draft.created_at,
  status: "current",
  sent_at: null,
  carried: false,
  shown_later: false,
});

export const AgentPaneConversation = ({
  conversation: c,
  hidden,
}: {
  conversation: PaneConversation;
  /** Another conversation's tab is in front; this one keeps running behind it. */
  hidden: boolean;
}) => {
  const pane = useAgentPane();
  const { user } = useKindeAuth();
  const projectName = usePaneProjectName(c.projectId);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const runId = pane.runOf(c);
  const starting = pane.startOf(c.key);
  const detailQuery = useStatefulRun(runId);
  const detail = runId ? detailQuery.data : undefined;
  const turn = useStatefulRunTurn(runId ?? "", c.projectId);
  const attachments = useAttachments();
  const liveTurn = detail?.turn ?? null;
  const answering = agentDisplayLabel(detail?.run.agent || c.agent);
  const drafting = c.intent !== "discuss";

  // Discuss waits on what the operator types, so its box takes the cursor whenever its
  // tab comes to the front.
  const box = useRef<HTMLTextAreaElement>(null);
  const quote = useComposerQuote(setMessage, box);
  useEffect(() => {
    if (!hidden && !drafting) box.current?.focus({ preventScroll: true });
  }, [hidden, drafting, c.key]);

  const { setWorking } = pane;
  const working = liveTurn !== null;
  useEffect(() => {
    setWorking(c.key, working);
  }, [setWorking, c.key, working]);

  useEffect(() => {
    if (drafting && !runId) pane.start(c);
    // Once, as the pane opens it: a failed start waits for Try again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Discuss's first message went with the start: once the run exists, it is in the
  // transcript, not still waiting in the box.
  const started = runId !== null;
  const firstSent = useRef("");
  useEffect(() => {
    if (started && !drafting) {
      setMessage((m) => leftAfterSend(m, firstSent.current));
      attachments.release(attachments.ready);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  const send = () => {
    if (!message.trim() || attachments.uploading || attachments.failed) return;
    if (!runId) {
      if (!drafting) {
        firstSent.current = message;
        pane.start(c, { content: message.trim(), ...attachments.ready });
      }
      return;
    }
    // While a turn runs the message is queued behind it.
    if (turn.isLoading) return;
    const sent = attachments.ready;
    const content = message.trim();
    const atSend = message;
    setError(null);
    turn.mutate(
      { content, actor_email: user?.email ?? "", ...sent },
      {
        onSuccess: () => {
          setMessage((m) => leftAfterSend(m, atSend));
          attachments.release(sent);
        },
        onError: (e) => setError(e.message),
      },
    );
  };

  const shownError = error ?? (runId ? null : starting.error);
  return (
    <section className={styles.conversation} hidden={hidden}>
      <header className={styles.head}>
        <div className={styles.headTop}>
          <nav
            className={styles.crumb}
            aria-label="Where this conversation is from"
          >
            <Link
              className={styles.project}
              to={`${projectSpecialistPath(c.projectId, c.agent)}#${c.actionId}`}
              title="Go to this action"
            >
              {projectName}
            </Link>
            <span aria-hidden>›</span>
            <span>{agentDisplayLabel(c.agent)}</span>
          </nav>
          <span className={styles.spacer} />
          <span className={styles.headActions}>
            {runId && (
              <Link
                className={styles.link}
                to={projectConversationPath(c.projectId, runId)}
              >
                Open in Conversations
              </Link>
            )}
            <button
              type="button"
              className={styles.button}
              onClick={() => pane.setMinimised(true)}
              title="Minimise - the agent keeps working"
            >
              Minimise
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => pane.close(c.key)}
            >
              Close
            </button>
          </span>
        </div>
        <h2 className={styles.title}>{c.title}</h2>
        <div className={styles.meta}>
          {paneActivity(c)} with {answering}
        </div>
      </header>
      <div className={styles.body}>
        <details className={styles.about}>
          <summary>About this {c.kind}</summary>
          <div className={styles.aboutBody}>{c.context}</div>
        </details>
        {c.email && (
          <EmailCard
            email={actionEmail(c.email)}
            onReply={null}
            inline={false}
            projectName={projectName}
            defaultOpen
          />
        )}
        {!runId && !drafting ? (
          !starting.pending && (
            <p className={styles.muted}>
              Ask {answering} about this {c.kind}. They will know which item you
              mean.
            </p>
          )
        ) : !runId || detailQuery.isLoading || !detail ? (
          starting.error ? null : (
            <p className={styles.muted}>Starting the conversation…</p>
          )
        ) : (
          <>
            <RunTranscript
              runId={runId}
              runKind={detail.run.kind}
              projectId={detail.run.context_project_id ?? null}
              seen={!hidden && !pane.minimised}
              messages={detail.messages}
              projects={[]}
              draftStatus={detail.draft_status}
              onReply={quote}
              openResponses={false}
              openEmails
            />
            {liveTurn && (
              <LiveTurnPanel
                turn={liveTurn}
                label={`${answering} is working…`}
                stopping={Boolean(liveTurn.cancel_requested)}
                onStop={() => undefined}
                fallbackAgent={answering}
              />
            )}
          </>
        )}
        {shownError && (
          <p className={styles.error}>
            {shownError}
            {drafting && !runId && (
              <>
                {" "}
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => pane.start(c)}
                >
                  Try again
                </button>
              </>
            )}
          </p>
        )}
      </div>
      <footer className={styles.foot}>
        <RunComposer
          value={message}
          onChange={setMessage}
          onSend={send}
          placeholder={
            runId
              ? `Reply to ${answering}…`
              : c.email
                ? `Ask ${answering} about this email…`
                : `Ask ${answering} about this…`
          }
          disabled={!runId && drafting}
          queueing={liveTurn !== null}
          sending={runId ? turn.isLoading : starting.pending}
          textareaRef={box}
          attachments={attachments}
        />
      </footer>
    </section>
  );
};
